import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Briefcase, AlertTriangle, ExternalLink, CreditCard, Crown } from "lucide-react";

const PLAN_ICONS = {
  starter: Zap,
  pro: Sparkles,
  business: Briefcase,
};

function fmtCents(c) {
  if (c == null) return "—";
  if (c % 100 === 0) return `$${(c / 100).toLocaleString()}`;
  return `$${(c / 100).toFixed(2)}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminBilling() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const wasCanceled = params.get("canceled") === "1";
  const autoPlan = params.get("plan");
  const autoInterval = params.get("interval");
  const autoStart = params.get("auto") === "1";

  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [status, setStatus] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [busyPlan, setBusyPlan] = useState("");
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/status"),
    ]);
    setPlans(p.data.plans || []);
    setTrialDays(p.data.trial_days || 14);
    setStatus(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPaid = useCallback(async () => {
    toast.success("Payment confirmed — subscription is now active!");
    setPolling(false);
    // Facebook Pixel — Subscribe conversion ONLY. StartTrial is fired once
    // at signup (SignupWizard) so we no longer fire it here to avoid
    // double-counting. Value is the actual plan price when we can resolve
    // it from the loaded plans list; falls back to the standard monthly
    // price (3999¢ = $39.99) when the user came in via a direct auto-
    // start URL and the plans list hasn't loaded yet. Safe no-op if the
    // Pixel script was blocked.
    const resolvedPlan = plans.find((p) => p.id === autoPlan);
    const priceCents = resolvedPlan
      ? (autoInterval === "yearly" ? resolvedPlan.yearly_price_cents : resolvedPlan.monthly_price_cents)
      : null;
    const value = priceCents != null ? priceCents / 100 : 39.99;
    try {
      if (typeof window !== "undefined" && typeof window.fbq === "function" && window.FB_PIXEL_ID && window.FB_PIXEL_ID !== "YOUR_PIXEL_ID") {
        window.fbq("track", "Subscribe", {
          content_name: autoPlan || "unknown",
          content_category: autoInterval || "monthly",
          value,
          currency: "USD",
        });
      }
    } catch {}
    setParams({}, { replace: true });
    await load();
  }, [setParams, load, autoPlan, autoInterval, plans]);

  const onExpired = useCallback(() => {
    toast.error("Checkout session expired. Please try again.");
    setPolling(false);
    setParams({}, { replace: true });
  }, [setParams]);

  // Post-payment status polling per Stripe playbook.
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      setPolling(true);
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { await onPaid(); return; }
        if (data.status === "expired") { onExpired(); return; }
        if (attempts >= 8) {
          toast.message("Still verifying payment… you'll see the update once Stripe confirms.");
          setPolling(false);
          return;
        }
        timer = setTimeout(tick, 2000);
      } catch (err) {
        if (cancelled) return;
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
        setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId, onPaid, onExpired]);

  useEffect(() => {
    if (!wasCanceled) return;
    toast.message("Checkout canceled — no charge made.");
    setParams({}, { replace: true });
  }, [wasCanceled, setParams]);

  const subscribe = useCallback(async (planId, intervalOverride) => {
    setBusyPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        interval: intervalOverride || interval,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusyPlan("");
    }
  }, [interval]);

  // Auto-start Stripe checkout if landed from signup with ?auto=1
  useEffect(() => {
    if (!autoStart || !autoPlan || !autoInterval || !status) return;
    if (status.is_active) return; // already on a plan
    setInterval(autoInterval);
    subscribe(autoPlan, autoInterval);
  }, [autoStart, autoPlan, autoInterval, status, subscribe]);

  const startTrial = async (planId) => {
    // Deprecated — no-card trial has been replaced by the card-required trial
    // baked into Stripe Checkout. This function is left as a redirect for any
    // stale UI still referencing it.
    return subscribe(planId);
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.href.split("?")[0],
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel your subscription? You'll keep access until the end of your current billing period.")) return;
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription set to cancel at period end");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const c = status?.company;
  const isActive = !!status?.is_active;
  const onTrial = c?.subscription_status === "trialing";
  const isComped = !!c?.comped;
  const hasStripeCustomer = !!c?.stripe_customer_id;

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Billing &amp; Plans</h1>
        <p className="text-sm text-slate-500 mt-1">Subscribe, upgrade, or manage your Production Pay Tracker plan.</p>
      </header>

      {polling && (
        <Card className="rounded-2xl border-sky-300 bg-sky-50" data-testid="billing-polling-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" />
            <p className="text-sm font-semibold text-sky-900">Verifying payment with Stripe…</p>
          </CardContent>
        </Card>
      )}

      {/* Current status panel */}
      <Card className="rounded-2xl border-slate-200" data-testid="billing-status-card">
        <CardContent className="p-5">
          {isComped && (
            <div className="mb-4 rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-4 flex items-start gap-3" data-testid="comped-banner">
              <Crown className="h-5 w-5 text-fuchsia-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-fuchsia-900">Complimentary account — no subscription needed</p>
                <p className="text-xs text-fuchsia-800 mt-0.5">
                  Your account has been comped by the platform owner. You have full access to every premium feature without any monthly billing.
                  {c?.comped_notes && <span className="block mt-1 italic">Note: {c.comped_notes}</span>}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Current Status</p>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <StatusBadge status={c?.subscription_status} />
                {c?.plan && (
                  <span className="text-base font-bold text-slate-900 capitalize">
                    {c.plan} <span className="font-normal text-slate-500">· {c.interval || "monthly"}</span>
                  </span>
                )}
              </div>
              {onTrial && (
                <p className="text-sm text-amber-700 mt-2">
                  Free trial ends <span className="font-semibold">{fmtDateTime(c?.trial_ends_at)}</span>. Subscribe below to keep access.
                </p>
              )}
              {isActive && !onTrial && (
                <p className="text-sm text-slate-600 mt-2">
                  Next renewal: <span className="font-semibold">{fmtDateTime(c?.period_end)}</span>
                </p>
              )}
              {c?.subscription_status === "canceled_at_period_end" && (
                <p className="text-sm text-red-700 mt-2">
                  Subscription cancels on <span className="font-semibold">{fmtDateTime(c?.period_end)}</span>.
                </p>
              )}
              {c?.subscription_status === "none" && (
                <p className="text-sm text-slate-600 mt-2">
                  <b>Your {trialDays}-day free trial is active — no credit card on file.</b> Pick a plan below whenever you're ready. On day {trialDays + 1} the account becomes read-only until you upgrade; your data is never deleted.
                </p>
              )}
            </div>
            {isActive && !isComped && hasStripeCustomer && (
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  onClick={openPortal}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="manage-billing-btn"
                >
                  <CreditCard className="h-4 w-4 mr-1.5" /> Manage billing
                  <ExternalLink className="h-3 w-3 ml-1 opacity-70" />
                </Button>
                {c?.subscription_status === "active" && (
                  <Button variant="outline" onClick={cancel} data-testid="cancel-subscription-btn">
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Interval toggle */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setInterval("monthly")}
          className={`px-5 py-2 rounded-full text-sm font-bold transition ${interval === "monthly" ? "bg-sky-600 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
          data-testid="interval-monthly"
        >
          Monthly
        </button>
        <button
          onClick={() => setInterval("yearly")}
          className={`px-5 py-2 rounded-full text-sm font-bold transition ${interval === "yearly" ? "bg-sky-600 text-white" : "bg-white border border-slate-300 text-slate-600"}`}
          data-testid="interval-yearly"
        >
          Yearly
          <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-green-100 text-green-700">Save 20%</span>
        </button>
      </div>

      {/* Plan grid */}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const priceCents = interval === "yearly" ? p.yearly_price_cents : p.monthly_price_cents;
          const isCurrentPlan = c?.plan === p.id && c?.interval === interval && isActive;
          return (
            <Card
              key={p.id}
              className={`rounded-2xl ${p.highlight ? "border-2 border-sky-500 shadow-lg shadow-sky-100" : "border-slate-200"} relative`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-sky-600 text-white text-[10px] font-bold uppercase tracking-wider">
                  Most popular
                </div>
              )}
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${p.highlight ? "text-sky-600" : "text-slate-700"}`} />
                  <h3 className="text-lg font-bold font-display">{p.name}</h3>
                </div>
                <div>
                  <p className="text-4xl font-extrabold font-display tracking-tighter text-slate-900">
                    {fmtCents(priceCents)}
                    <span className="text-base font-medium text-slate-500">/{interval === "yearly" ? "yr" : "mo"}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {p.employee_max ? `Up to ${p.employee_max} employees` : "Unlimited employees"}
                  </p>
                </div>
                <ul className="space-y-1.5 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <span className="text-slate-700">{f}</span>
                    </li>
                  ))}
                </ul>
                {isCurrentPlan ? (
                  <Button disabled className="w-full bg-green-600 hover:bg-green-600 text-white">
                    <Check className="h-4 w-4 mr-1" /> Current Plan
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button
                      onClick={() => subscribe(p.id)}
                      disabled={busyPlan === p.id}
                      className={`w-full ${p.highlight ? "bg-sky-600 hover:bg-sky-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}
                      data-testid={`subscribe-${p.id}`}
                    >
                      {busyPlan === p.id
                        ? "Redirecting…"
                        : (isActive
                            ? `Switch to ${p.name}`
                            : `Start ${trialDays}-day free trial`)}
                      <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                    {!isActive && (
                      <p className="text-[10px] text-center text-slate-500 leading-relaxed">
                        Card required · You&apos;re not charged for {trialDays} days · Cancel anytime<br/>
                        By continuing, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline decoration-2 underline-offset-2 text-sky-700 hover:text-sky-900" data-testid="billing-terms-link">Terms</a> &amp; <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline decoration-2 underline-offset-2 text-sky-700 hover:text-sky-900" data-testid="billing-privacy-link">Privacy Policy</a>.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Refund policy — surfaced prominently on the billing page (not just
          buried in Terms) so users have a clear expectation before they
          subscribe. Meta ad reviewers also check for this. */}
      <Card className="rounded-2xl border-slate-200 bg-slate-50" data-testid="refund-policy-card">
        <CardContent className="p-5 text-xs text-slate-600 leading-relaxed">
          <p className="font-bold text-slate-900 text-sm mb-1">Refund policy</p>
          <p>
            Your {trialDays}-day free trial requires a card but is <b>never charged</b> — cancel any
            time before day {trialDays + 1} at no cost. After that, subscriptions are billed in
            advance and are <b>non-refundable</b> except where required by law. If you believe
            you were charged in error, email <a href="mailto:support@productionpaytracker.com" className="text-sky-700 underline decoration-2 underline-offset-2">support@productionpaytracker.com</a> within
            30 days and we&apos;ll investigate in good faith. Cancel anytime from &ldquo;Manage
            billing&rdquo; above — access continues through the end of the paid period.
          </p>
        </CardContent>
      </Card>

      {/* Stripe mode diagnostic — internal ops copy. Only shown to
          super_admin so real tenants never see technical Stripe-mode
          language on their trial-signup screen. */}
      {isSuperAdmin && (
        <Card className="rounded-2xl border-slate-200 bg-slate-50">
          <CardContent className="p-5 flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-600 leading-relaxed" data-testid="stripe-mode-note">
              {status?.stripe_api_key_mode === "live" ? (
                <>
                  <p className="font-semibold text-emerald-700 mb-1">Live mode — real charges</p>
                  <p>
                    This deployment is connected to your Stripe <b>live</b> account. Every subscription started here creates a real customer and (after the {trialDays}-day trial) real charges. Manage cards, invoices, and cancellations from the customer portal.
                  </p>
                </>
              ) : status?.stripe_api_key_mode === "test" ? (
                <>
                  <p className="font-semibold text-slate-700 mb-1">Test mode — no real charges</p>
                  <p>
                    This deployment is connected to your Stripe <b>test mode</b>. Use test card <code className="px-1 bg-slate-200 rounded">4242 4242 4242 4242</code>, any future expiry, and any CVC. Cards are captured but no real charges are ever made until you switch to a <b>live</b> key.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-amber-700 mb-1">Stripe mode: unknown</p>
                  <p>
                    Your <code className="px-1 bg-slate-200 rounded">STRIPE_API_KEY</code> is set but doesn&apos;t look like a standard live/test Stripe key. Verify it in your deployment env vars and redeploy.
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  none: "bg-slate-200 text-slate-700",
  trialing: "bg-amber-100 text-amber-800",
  active: "bg-green-100 text-green-700",
  past_due: "bg-red-100 text-red-700",
  canceled: "bg-slate-300 text-slate-700",
  canceled_at_period_end: "bg-red-100 text-red-700",
};

function StatusBadge({ status }) {
  const label = (status || "none").replace(/_/g, " ");
  const cls = STATUS_STYLES[status] || STATUS_STYLES.none;
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid="billing-status-badge">
      {label}
    </span>
  );
}
